import clsx from 'clsx'

import { useMemo } from 'preact/hooks'
import { useBeaconLogClick } from 'libs/searchspring/hooks/useBeaconAnalytics'
import { scrollHistoryManager } from 'helpers/scroll-history'

import { ResultImage } from 'libs/searchspring/components/ResultImage'
import { Price } from 'libs/searchspring/components/Price'
import { useBadges } from '@/components/hooks/useBadges'
import { Badge } from 'libs/searchspring/components/Badge'
import { Promoline } from 'libs/searchspring/components/Promoline'
import { usePromolines } from '@/components/hooks/usePromolines'
import { useSwatches } from 'libs/searchspring/hooks/useSwatches'
import { ResultsSwatches } from 'libs/searchspring/components/ResultSwatches'

const DEFAULT_OPTIONS = {
  hideQuickAdd: true,
  hideSwatches: false,
  hidePromolines: false,
  hideBadges: false,
  hideWishlist: false
}

export function ResultsCard({ result, className, options = DEFAULT_OPTIONS, dataIndex = '0' }) {
  const {
    hideSwatches = DEFAULT_OPTIONS.hideSwatches,
    hidePromolines = DEFAULT_OPTIONS.hidePromolines,
    hideBadges = DEFAULT_OPTIONS.hideBadges,
    hideWishlist = DEFAULT_OPTIONS.hideWishlist
  } = options
  const { currentResult, updateSwatch } = useSwatches(result)
  const clickLogger = useBeaconLogClick()

  // Determine which name to use - custom field with fallback to regular name
  const displayName = currentResult.mfield_utopia_customer_facing_product_name || currentResult.name

  const criteria = useMemo(() => ({
    tags: currentResult?.tags ?? [],
    collections: currentResult?.collection_handle ?? [],
    products: [currentResult.handle]
  }), [currentResult?.tags, currentResult?.collection_handle, currentResult?.handle])

  // Metaobject variables
  const allBadges = useBadges(criteria)
  const allPromolines = usePromolines(criteria)

  // Generic function to get first two items with exclusive logic
  const getObjects = (items) => {
    const exclusiveItem = items.find(item => item.exclusive)
    if (exclusiveItem) {
      return [exclusiveItem, null]
    }
    return [items[0] || null, items[1] || null]
  }

  const [firstBadge, secondBadge] = useMemo(() => getObjects(allBadges), [allBadges])
  const [firstPromoline, secondPromoline] = useMemo(() => getObjects(allPromolines), [allPromolines])

  const filteredSwatches = useMemo(() =>
    currentResult.swatches?.filter(swatch =>
      !swatch.tags?.includes('Flow:OutOfStock')
    ) || []
  , [currentResult.swatches])

  // Creates URL with collection handle reference if it exists
  const url = useMemo(() => {
    return `/products/${currentResult.handle}`
  }, [currentResult])

  // Analytics handling
  const handleLogClick = () => {
    // Collection is a global pulled from template-search.liquid and collection-main.liquid. Ensure value is set in both places :)
    scrollHistoryManager.updateHistoryStateWithCollectionInfo(currentResult)
    clickLogger(currentResult)
  }

  // This is wrapped in an extra div to support injection from Wishlist KING
  // DON'T REMOVE THAT DIV 🔫
  return (
    <div className={clsx('group/card relative flex', className)} id={`result__${currentResult.handle}`} data-dl-track="select_item" data-dl-product-id={currentResult.uid} data-dl-index={dataIndex}>
      <div className="flex flex-col gap-y-1 w-full grow">
        <div className="relative w-full overflow-hidden aspect-1/1">
          <a href={url} onClick={handleLogClick} aria-label={currentResult.name}>
            <ResultImage result={currentResult}>
            </ResultImage>
          </a>
          {((!hideBadges && (firstBadge || secondBadge)) || (!hidePromolines && (firstPromoline || secondPromoline))) && (
            <div className="absolute top-0.75 md:top-0.875 left-0.75 md:left-0.875 flex flex-wrap gap-x-0.625 gap-y-0.125 max-w-[80%] z-2">
              {!hideBadges && (firstBadge || secondBadge) && (
                <>
                  {firstBadge && <Badge badge={firstBadge} badgeLocation="PLP" product={currentResult} />}
                  {secondBadge && <Badge badge={secondBadge} badgeLocation="PLP" product={currentResult} />}
                </>
              )}
              {!hidePromolines && (firstPromoline || secondPromoline) && (
                <>
                  {firstPromoline && <Promoline promoline={firstPromoline} className="lg-max:hidden" promolineSize="small" />}
                  {secondPromoline && <Promoline promoline={secondPromoline} className="lg-max:hidden" promolineSize="small" />}
                </>
              )}
            </div>
          )}
          {/* WishlistKing Button */}
          {!hideWishlist && (
            <div className="wishlist-button flex items-center justify-center absolute inset-tr md:top-0.25 lg:right-0.25 w-[44px] aspect-1/1 opacity-0 group-hover/card:opacity-100 transition-all z-2">
              <button type="button"
                data-product-handle={currentResult.handle}
                aria-label="Add to Wishlist"
                className="wkh-button save-wishlist flex items-center justify-center size-full link-fade"
              >
                <div className="w-[15px] h-[14px] children:size-full">
                  <svg class="wkh-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="19" viewBox="0 0 20 19" fill="none">
                    <path d="M16.4197 10.8342C17.7862 9.49523 19.171 7.89031 19.171 5.79015C19.171 4.45238 18.6396 3.16941 17.6936 2.22346C16.7477 1.27752 15.4647 0.746094 14.1269 0.746094C12.5128 0.746094 11.3756 1.20464 9.99999 2.5803C8.62434 1.20464 7.48713 0.746094 5.87303 0.746094C4.53527 0.746094 3.25229 1.27752 2.30635 2.22346C1.36041 3.16941 0.828979 4.45238 0.828979 5.79015C0.828979 7.89948 2.20463 9.50441 3.58028 10.8342L9.99999 17.2539L16.4197 10.8342Z" fill="var(--icon-fill)" stroke="context-stroke" stroke-width="context-stroke-width"/>
                  </svg>
                </div>
              </button>
            </div>
          )}
        </div>
        <div className="grow flex flex-col gap-y-0.5 px-0.5 lg:px-1">
          <div className="flex lg-max:flex-col justify-between gap-x-1 gap-y-0.25">
            <a
              href={url}
              className="paragraph-s font-bold link-fade"
              aria-label={displayName}
              dangerouslySetInnerHTML={{ __html: displayName }}
            />
          <Price price={currentResult.variants[0].price} compareAtPrice={currentResult.variants[0].compare_at_price} className="flex gap-x-0.125" />
          </div>
          <div className="flex lg-max:flex-col justify-between gap-x-1 gap-y-0.5 pb-0.125">
            {(!hideSwatches) && (
              <div className="grid auto-rows-min items-start justify-start children:col-[1] children:row-[1]">
                <div className="flex opacity-0 group-hover/card:opacity-100 transition-all">
                  <ResultsSwatches result={currentResult} swatches={result.swatches} onSelectSwatch={updateSwatch} />
                </div>
                <div className="flex size-full bg-secondary group-hover/card:opacity-0 pointer-events-none transition-all">
                  <span className="text-xs">
                    {filteredSwatches[0]?.colourName
                      ? filteredSwatches[0].colourName
                        .split(' ')
                        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                        .join(' ')
                      : ''}
                    {filteredSwatches.length > 1 && (
                      <span className="text-neutral-tertiary whitespace-nowrap"> <span className="lg-max:hidden xl:hidden">+{filteredSwatches.length - 1}</span><span className="lg:hidden xl:inline">{filteredSwatches.length} Colours</span></span>
                    )}
                  </span>
                </div>
              </div>
            )}
            <klaviyo-star-rating
              className="min-h-1.125"
              product-id={currentResult.uid}
              product-title={currentResult.name}
              product-type={currentResult.type}
            ></klaviyo-star-rating>
          </div>
          {!hidePromolines && (firstPromoline || secondPromoline) && (
            <div className="flex lg:hidden flex-col gap-0.25">
              {firstPromoline && <Promoline promoline={firstPromoline} />}
              {secondPromoline && <Promoline promoline={secondPromoline} />}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
